/**
 * The navigation guard's decision logic, deliberately free of React so it can be tested
 * directly — the admin suite runs without jsdom (see `vitest.config.ts`).
 *
 * The rule it encodes: a navigation only runs while nothing on screen is holding unsaved
 * edits. Anything else is held back and the merchant is asked, and it runs only once they
 * discard. There is deliberately no "leave anyway" — leaving with the edits intact but
 * unreachable is the behaviour this replaces, and it is how a merchant loses a form design
 * they think they configured.
 *
 * 🚨 Every screen registers by id rather than there being one global flag: the Quantity
 * Offers list stays mounted behind the offer editor it opens, so two surfaces really can be
 * registered at once.
 */

/** Puts one screen's edits back to what the server has. Supplied by that screen. */
export type DiscardEdits = () => void;

/** Anything that takes the merchant away from what they are editing. */
export type NavigationAction = () => void;

export interface NavigationGuard {
  /** Register (or update) one screen's unsaved state. Clean screens are dropped. */
  setDirty(id: string, dirty: boolean, discard: DiscardEdits): void;
  /** Forget a screen entirely — it unmounted. */
  release(id: string): void;
  /** Whether anything on screen currently holds unsaved edits. */
  isBlocked(): boolean;
  /** Whether a navigation is waiting on the merchant's answer. */
  isPrompting(): boolean;
  /** Run `action` now, or hold it and prompt when something is unsaved. */
  request(action: NavigationAction): void;
  /** "Discard changes": revert every registered screen, then run the held navigation. */
  discardAndContinue(): void;
  /** "Keep editing": forget the held navigation and change nothing. */
  cancel(): void;
}

export function createNavigationGuard(notify: () => void): NavigationGuard {
  // Only DIRTY screens live in here, so `size > 0` answers the whole question.
  const dirtyScreens = new Map<string, DiscardEdits>();
  let pending: NavigationAction | null = null;

  const blocked = (): boolean => dirtyScreens.size > 0;

  return {
    setDirty(id, dirty, discard) {
      const was = blocked();
      if (dirty) dirtyScreens.set(id, discard);
      else dirtyScreens.delete(id);
      if (was !== blocked()) notify();
    },

    release(id) {
      const was = blocked();
      dirtyScreens.delete(id);
      if (was !== blocked()) notify();
    },

    isBlocked: blocked,

    isPrompting: () => pending !== null,

    request(action) {
      if (!blocked()) {
        action();
        return;
      }
      pending = action;
      notify();
    },

    discardAndContinue() {
      const action = pending;
      const discards = [...dirtyScreens.values()];
      pending = null;
      // Cleared up front rather than waiting for each screen's effect to re-register a render
      // later: the navigation below must not observe the guard it has just satisfied as still
      // blocked. A screen whose discard did not actually clean it re-registers itself anyway.
      dirtyScreens.clear();
      notify();
      // Edits are reverted BEFORE the navigation, so a screen is back at its saved state
      // before it is taken away — not left half-reverted in a component that is unmounting.
      discards.forEach((discard) => discard());
      action?.();
    },

    cancel() {
      if (pending === null) return;
      pending = null;
      notify();
    },
  };
}
